import React, { Suspense, lazy, memo, useMemo } from 'react';
import { BlogPost, BlogMeta } from '@/services/blogService';
import { BlogHeader } from './BlogHeader';
import { BlogContent } from './BlogContent';
import { BlogNavigation } from './BlogNavigation';
import { BlogFooter } from './BlogFooter';
// Performance monitoring removed for production
import { SkipToContent } from '../BlogUtils/AccessibilityUtils';
import { ResponsiveContainer, useResponsive } from '../BlogUtils/ResponsiveUtils';
import { MobileTableOfContents } from '../BlogUtils/MobileTableOfContents';

// Lazy load components for better performance with optimized imports
const TableOfContents = lazy(() => 
  import('../BlogUtils/TableOfContents').then(module => ({ 
    default: module.TableOfContents 
  }))
);

const ShareButtons = lazy(() => 
  import('../BlogUtils/ShareButtons').then(module => ({ 
    default: module.ShareButtons 
  }))
);

const EngagementTracker = lazy(() => 
  import('../BlogUtils/EngagementTracker').then(module => ({ 
    default: module.EngagementTracker 
  }))
);

interface BlogTemplateProps {
  post: BlogPost;
  relatedPosts: BlogMeta[];
  children?: React.ReactNode;
}

interface BlogTemplateConfig {
  showTableOfContents: boolean;
  showProgressIndicator: boolean;
  showSocialSharing: boolean;
  showRelatedPosts: boolean;
  maxContentWidth: string;
  enableSmoothScrolling: boolean;
}

const defaultConfig: BlogTemplateConfig = {
  showTableOfContents: true,
  showProgressIndicator: true,
  showSocialSharing: true,
  showRelatedPosts: true,
  maxContentWidth: '4xl',
  enableSmoothScrolling: true,
};

export const BlogTemplate: React.FC<BlogTemplateProps> = memo(({
  post,
  relatedPosts,
  children,
}) => {
  // Performance optimized
  const { isMobile, isTablet } = useResponsive();
  const config = defaultConfig; // Could be made configurable later

  // Generate TOC items for mobile component
  const tocItems = useMemo(() => {
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const items: Array<{ id: string; title: string; level: number }> = [];
    let match;

    while ((match = headingRegex.exec(post.content)) !== null) {
      const level = match[1].length;
      const title = match[2].trim();
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      
      items.push({ id, title, level });
    }

    return items;
  }, [post.content]);

  const [activeId, setActiveId] = React.useState<string>('');

  const handleTocItemClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = isMobile ? 80 : 120;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Skip to content link for keyboard users */}
      <SkipToContent targetId="main-content" />
      
      {/* Progress Indicator */}
      {config.showProgressIndicator && <BlogNavigation post={post} config={config} />}
      
      {/* Mobile Table of Contents */}
      {config.showTableOfContents && (
        <MobileTableOfContents
          tocItems={tocItems}
          activeId={activeId}
          onItemClick={handleTocItemClick}
        />
      )}
      
      <article className="relative">
        <ResponsiveContainer maxWidth="2xl" className={isMobile ? 'px-4' : 'px-6'}>
          {/* Blog Header */}
          <header>
            <BlogHeader post={post} config={config} />
          </header>
          
          {/* Main Content Area */}
          <div className={`${isMobile ? 'block' : 'grid grid-cols-1 lg:grid-cols-12 gap-8'} relative`}>
            {/* Table of Contents - Desktop Only */}
            {config.showTableOfContents && !isMobile && !isTablet && (
              <aside 
                className="lg:col-span-3 xl:col-span-2"
                aria-label="Table of contents navigation"
              >
                <div className="sticky top-32">
                  <Suspense fallback={
                    <div 
                      className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 animate-pulse"
                      aria-label="Loading table of contents"
                    >
                      <div className="h-4 bg-white/10 rounded mb-4"></div>
                      <div className="space-y-2">
                        <div className="h-3 bg-white/5 rounded"></div>
                        <div className="h-3 bg-white/5 rounded"></div>
                        <div className="h-3 bg-white/5 rounded"></div>
                      </div>
                    </div>
                  }>
                    <TableOfContents content={post.content} />
                  </Suspense>
                </div>
              </aside>
            )}
            
            {/* Main Content */}
            <main 
              id="main-content"
              className={`${
                config.showTableOfContents && !isMobile && !isTablet 
                  ? 'lg:col-span-9 xl:col-span-10' 
                  : 'w-full'
              }`}
              role="main"
              aria-label={`Blog post: ${post.title}`}
            >
              <BlogContent post={post} config={config}>
                {children}
              </BlogContent>
            </main>
          </div>
          
          {/* Blog Footer */}
          {config.showRelatedPosts && (
            <footer>
              <BlogFooter post={post} relatedPosts={relatedPosts} config={config} />
            </footer>
          )}
        </ResponsiveContainer>
      </article>
    </div>
  );
});

BlogTemplate.displayName = 'BlogTemplate';

export default BlogTemplate;